import { useState, useCallback, useEffect } from "react";
import SetSaleBillBottom from "./SetSaleBillBottom";
import SetSaleBillMiddle from "./SetSaleBillMiddle";
import SetSaleBillTop from "./SetSaleBillTop";
import AddonsTable from "./AddonsTable";
import BillTotals from "./BillTotals";
import AgentCommission from "./AgentCommission";
import CaseDiversionModal from "./CaseDiversionModal";
import Modal from "components/ui/modals";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useBill } from "hooks/bill";
import useFullScreenLayout from "hooks/useFullScreenLayout";

export default function SetSaleBill() {
  useFullScreenLayout();

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id || location.state?.id || null;
  const isEditMode = !!id;
  
  // State to receive calculated values from SetSaleBillBottom
  const [calculatedValues, setCalculatedValues] = useState({
    totalAmountAfterAllDiscounts: 0,
    freightAfterTax: 0,
    tcsAmount: 0,
  });

  // Get bill state and mutation functions from hook
  const {
    billHeader,
    tableData,
    totalGross,
    totalTax,
    totalQtyPcs,
    selectBoxes,
    isIGST,
    beforeTaxDiscounts,
    afterTaxDiscounts,
    agentCommission,
    taxBreakdown,
    traderBillType,
    confirmpltransfer,
    showCaseDiversionModal,
    setShowCaseDiversionModal,
    createSaleBill,
    isCreatingSaleBill,
    updateSaleBill,
    isUpdatingSaleBill,
    resetStore,
  } = useBill();

  // Reset store when component unmounts or id changes
  useEffect(() => {
    return () => {
      resetStore();
    };
  }, [resetStore]);

  const handleSubmitForm = async () => {
    // Validate bill type
    if (!selectBoxes.billType || !["DIR", "STK", "PL", "ORD"].includes(selectBoxes.billType.value)) {
      console.warn("Invalid bill type. Supported: DIR, STK, PL, ORD");
      return;
    }

    // Prepare structured data for API
    const structuredData = {
      billHeader,
      tableData: tableData.filter(item => item.selected),
      selectBoxes,
      traderbilltyp: traderBillType,
      settlementMode: billHeader.settlementMode,
      cashCustomer: billHeader.cashCustomer || {
        cname: billHeader.cashCustomerName,
      },
      confirmpltransfer,
      docType: "AGENTBILL",
      calculations: {
        totalGross,
        totalTax,
        totalQtyPcs,
        isIGST,
        ...taxBreakdown,
      },
      discounts: beforeTaxDiscounts,
      afterTaxDiscounts,
      agentCommission,
      additionalAmounts: {
        tcsAmount: calculatedValues.tcsAmount || 0,
        tcsPercentage: selectBoxes.vendor?.tcsperc || 0,
        freightAfterTax: calculatedValues.freightAfterTax || 0,
        totalAmountAfterAllDiscounts: calculatedValues.totalAmountAfterAllDiscounts || 0,
      }
    };

    try {
      if (isEditMode) {
        await updateSaleBill({ id, data: structuredData });
      } else {
        await createSaleBill(structuredData);
      }
      navigate(`/salebill${location.search}`);
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} sale bill:`, error);
    }
  };

  // Handle calculated values from SetSaleBillBottom
  const handleCalculatedValues = useCallback((values) => {
    setCalculatedValues(values);
  }, []);

  return (
    <form>
      <div className="space-y-8 px-4 sm:px-6 lg:px-8">
        <SetSaleBillTop id={id} docType="AGENTBILL" />
        <SetSaleBillMiddle docType="AGENTBILL" />
        {selectBoxes.billType?.value === "ORD" && (
          <AddonsTable docType="AGENTBILL" />
        )}
        <BillTotals docType="AGENTBILL" />
        <SetSaleBillBottom
          totalGross={totalGross}
          totalTax={totalTax}
          isIGST={isIGST}
          docType="AGENTBILL"
          handleSubmitForm={handleSubmitForm}
          onCalculatedValues={handleCalculatedValues}
          isSubmitting={isCreatingSaleBill || isUpdatingSaleBill}
          isEditMode={isEditMode}
        />
        <AgentCommission
          totalAmountAfterAllDiscounts={calculatedValues.totalAmountAfterAllDiscounts}
          totalTax={totalTax}
          tcsAmount={calculatedValues.tcsAmount}
          freightAfterTax={calculatedValues.freightAfterTax}
          docType="AGENTBILL"
        />
      </div>

      {/* Case Diversion Modal - Only for PL bills */}
      <Modal
        open={showCaseDiversionModal && selectBoxes.billType?.value === "PL"}
        setOpen={setShowCaseDiversionModal}
      >
        <CaseDiversionModal />
      </Modal>
    </form>
  );
}
